import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  Loader2, ArrowRight, ArrowLeft, AlertCircle, Clock, 
  XCircle, RotateCcw, CalendarClock, Undo2, CheckCircle2,
  Info, ClipboardCheck, Unlock, DollarSign, Receipt, Archive,
  AlertTriangle, MessageSquarePlus, Flag
} from 'lucide-react';
import {
  SR_STATUS_CONFIG,
  WS_STATUS_CONFIG,
  WS_STATUS_ORDER,
  getValidSRTransitions,
  getValidWSTransitions,
  getBillingCorrections,
  getInvoicedCorrections,
  transitionRequiresNote,
  canSetCancelled,
  canSetCancelledForRole,
  canSetUncancel,
  canSetReschedule,
  SR_FOLLOWUP_THRESHOLDS,
  getTechnicianLockedMessage
} from '@/config/statusWorkflow';

/**
 * Status Change Modal for Service Requests and Work Schedules
 * 
 * Features:
 * - Role-based status transitions (Technician vs Operator+)
 * - COMPLETE status (requires 100% line items done)
 * - CANCELLED action (Operator+ with required reason)
 * - UNCANCEL action (Manager+ restores previous status)
 * - RESCHEDULE action (requires reason, locks job for technicians)
 */

// Pure helper — pulled outside the component so it doesn't get re-created per render.
const getTransitionDirection = (entityType, currentStatus, toStatus) => {
  if (entityType === 'ws') {
    const currentIdx = WS_STATUS_ORDER.indexOf(currentStatus);
    const toIdx = WS_STATUS_ORDER.indexOf(toStatus);
    return toIdx > currentIdx ? 'forward' : 'backward';
  }
  const srOrder = ['NEW', 'ESTIMATE', 'APPROVAL', 'PLANNING', 'SR_SCHEDULED'];
  const currentIdx = srOrder.indexOf(currentStatus);
  const toIdx = srOrder.indexOf(toStatus);
  return toIdx > currentIdx ? 'forward' : 'backward';
};

// Resolves the visual style for the submit button based on the selected status.
const submitButtonClass = ({ selectedStatus, billingCorrections, invoicedCorrections }) => {
  if (selectedStatus === 'CANCELLED') return 'bg-red-600 hover:bg-red-700 text-white';
  if (billingCorrections.includes(selectedStatus) || invoicedCorrections.includes(selectedStatus)) {
    return 'bg-amber-600 hover:bg-amber-700 text-white';
  }
  return 'bg-[#005EF8] hover:bg-[#0047C2] text-white';
};

// Resolves the human-readable submit-button label based on context.
const submitButtonLabel = ({ selectedStatus, currentStatus, billingCorrections, invoicedCorrections }) => {
  if (selectedStatus === 'CANCELLED') return 'Cancel Job';
  if (selectedStatus === 'COMPLETE' && currentStatus === 'INCOMPLETE') return 'Override to Complete';
  if (billingCorrections.includes(selectedStatus) || invoicedCorrections.includes(selectedStatus)) {
    return 'Apply Correction';
  }
  return 'Change Status';
};

// 2026-08-22 — Billing Issue overlay section (WS Billing only).
// Not a status transition. Two modes: no active issue -> "Flag Billing
// Issue" (opens a note prompt); active issue -> shows details + "Resolve
// Billing Issue" (also note-required). WS status never changes here.
const BillingIssueSection = ({ activeIssue, onFlag, onResolve }) => {
  const [mode, setMode] = useState(null); // null | 'flag' | 'resolve'
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const startFlag = () => { setMode('flag'); setNote(''); };
  const startResolve = () => { setMode('resolve'); setNote(''); };
  const cancel = () => { setMode(null); setNote(''); };

  const confirm = async () => {
    const trimmed = note.trim();
    if (!trimmed) {
      toast.error(mode === 'flag' ? 'Billing issue note is required' : 'Resolution note is required');
      return;
    }
    setBusy(true);
    try {
      if (mode === 'flag') await onFlag?.(trimmed);
      else await onResolve?.(trimmed);
      setMode(null);
      setNote('');
    } catch (e) {
      // Errors are already surfaced as toasts by the parent callback;
      // swallow here so we never leak a React error overlay. The parent
      // also re-fetches active issue state on failure paths.
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="billing-issue-section">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Flag className={`w-4 h-4 ${activeIssue ? 'text-amber-600 fill-amber-500' : 'text-amber-600'}`} aria-hidden="true" />
            <span className="text-sm font-semibold text-amber-900">
              Billing Issue{activeIssue ? ' — Active' : ''}
            </span>
          </div>
          {activeIssue && (
            <div className="mt-1 text-xs text-amber-900">
              <div className="whitespace-pre-wrap"><span className="font-medium">Note:</span> {activeIssue.issue_note}</div>
              <div className="mt-1"><span className="font-medium">Flagged by:</span> {activeIssue.flagged_by_name}</div>
              <div><span className="font-medium">At:</span> {activeIssue.flagged_at ? new Date(activeIssue.flagged_at).toLocaleString() : '—'}</div>
            </div>
          )}
        </div>
        {mode === null && (
          activeIssue
            ? <Button size="sm" variant="outline" onClick={startResolve} data-testid="btn-resolve-billing-issue">Resolve Billing Issue</Button>
            : <Button size="sm" variant="outline" onClick={startFlag} data-testid="btn-flag-billing-issue">Flag Billing Issue</Button>
        )}
      </div>
      {mode !== null && (
        <div className="mt-3 space-y-2">
          <Label htmlFor="billing-issue-note" className="text-xs text-amber-900">
            {mode === 'flag' ? 'Billing issue note *' : 'Resolution note *'}
          </Label>
          <Textarea
            id="billing-issue-note"
            data-testid="billing-issue-note-input"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder={mode === 'flag' ? 'Describe the billing issue' : 'Describe how it was resolved'}
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={cancel} disabled={busy} data-testid="billing-issue-cancel">Cancel</Button>
            <Button
              size="sm"
              onClick={confirm}
              disabled={busy}
              className={mode === 'flag' ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
              data-testid="billing-issue-confirm"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : (mode === 'flag' ? 'Flag' : 'Resolve')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

// Small subcomponent for the cancel-confirmation alert dialog.
const CancelConfirmDialog = ({ open, onOpenChange, entityType, reason, onGoBack, onConfirm }) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle className="flex items-center gap-2 text-red-600">
          <XCircle className="w-5 h-5" />
          Confirm Cancellation
        </AlertDialogTitle>
        <AlertDialogDescription>
          Are you sure you want to cancel this {entityType === 'sr' ? 'service request' : 'work schedule'}?
          This is a final action and can only be undone by a Manager or Admin.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <div className="py-4">
        <p className="text-sm text-gray-700 mb-2">
          <strong>Reason:</strong> {reason}
        </p>
      </div>
      <AlertDialogFooter>
        <AlertDialogCancel onClick={onGoBack}>Go Back</AlertDialogCancel>
        <AlertDialogAction
          onClick={onConfirm}
          className="bg-red-600 hover:bg-red-700"
          data-testid="confirm-cancel-btn"
        >
          Yes, Cancel It
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

const StatusChangeModal = ({
  open,
  onOpenChange,
  entityType = 'sr', // 'sr' or 'ws'
  currentStatus,
  previousStatus = null, // For UNCANCEL
  entityId,
  entityLabel = '',
  daysInStatus = 0,
  userRole = 'OPERATOR',
  onStatusChange,
  api,
  // New props for line item completion check
  lineItemsComplete = false,
  totalLineItems = 0,
  completedLineItems = 0,
  // v1 Railcar Repair Status Notification — WS-only hook. Called when
  // the user confirms COMPLETE from an active (non-INCOMPLETE) status.
  // Parent should open the required snapshot form. Snapshot POST flips
  // the WS status to COMPLETE server-side.
  onCompleteIntercept = null,
  // 2026-08-22 Billing Issue overlay (WS only, current status = BILLING).
  // Modal self-fetches active issue on open so state is always fresh
  // between opens. Parent only needs to expose Flag/Resolve endpoints.
  onFlagBillingIssue = null,
  onResolveBillingIssue = null,
}) => {
  // Active issue local to the modal — refetched every time it opens.
  const [activeBillingIssue, setActiveBillingIssue] = useState(null);
  const _refreshActiveIssue = useCallback(async () => {
    if (entityType !== 'ws' || currentStatus !== 'BILLING' || !entityId || !api) return;
    try {
      const r = await api.get(`/work-schedules/${entityId}/billing-issue`);
      setActiveBillingIssue(r?.data?.active_issue || null);
    } catch { setActiveBillingIssue(null); }
  }, [entityType, currentStatus, entityId, api]);
  useEffect(() => {
    if (open) _refreshActiveIssue();
    else setActiveBillingIssue(null);
  }, [open, _refreshActiveIssue]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [notes, setNotes] = useState('');
  const [overrideNote, setOverrideNote] = useState('');
  const [changing, setChanging] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [noteOnlyMode, setNoteOnlyMode] = useState(false);
  const [statusNote, setStatusNote] = useState('');

  const statusConfig = entityType === 'sr' ? SR_STATUS_CONFIG : WS_STATUS_CONFIG;
  
  // Get valid transitions based on role and line item completion
  // For INCOMPLETE → COMPLETE override, we don't require lineItemsComplete
  const isTechnician = userRole === 'TECHNICIAN';
  const isIncompleteOverride = currentStatus === 'INCOMPLETE' && !isTechnician;
  const validTransitions = entityType === 'sr' 
    ? getValidSRTransitions(currentStatus, userRole)
    : getValidWSTransitions(currentStatus, userRole, isIncompleteOverride ? true : lineItemsComplete);

  // 2026-08-22 — while a Billing Issue is active, hide the INVOICED
  // move-to option (server also blocks it as defense-in-depth).
  const _validTransitions = (entityType === 'ws' && currentStatus === 'BILLING' && activeBillingIssue)
    ? validTransitions.filter((s) => s !== 'INVOICED')
    : validTransitions;

  const currentConfig = statusConfig[currentStatus] || { label: currentStatus, color: 'bg-gray-100 text-gray-700' };
  
  // Check special actions availability based on role
  const canCancel = entityType === 'ws' 
    ? canSetCancelledForRole(currentStatus, entityType, userRole)
    : canSetCancelled(currentStatus, entityType, userRole);
  const canUncancelStatus = canSetUncancel(currentStatus, userRole, previousStatus);
  
  // PROMPT 2: Reschedule availability
  // - For early statuses (SCHEDULED, IN_PROCESS, ON_HOLD): shown in Special Actions
  // - For INCOMPLETE: shown in Move to section (already in validTransitions)
  // - For COMPLETE, QA_REVIEW, RELEASE, INVOICED: shown in Corrections section (requires note)
  const earlyStageStatuses = ['SCHEDULED', 'IN_PROCESS', 'ON_HOLD'];
  const postCompleteStatuses = ['COMPLETE', 'QA_REVIEW', 'RELEASE', 'INVOICED'];
  const isRescheduleInMoveToSection = currentStatus === 'INCOMPLETE' && !isTechnician;
  const isRescheduleInCorrections = postCompleteStatuses.includes(currentStatus) && !isTechnician;
  const canRescheduleSpecialAction = entityType === 'ws' && 
    earlyStageStatuses.includes(currentStatus) && 
    canSetReschedule(currentStatus) && 
    !isRescheduleInMoveToSection;
  
  // Billing corrections - pull back for errors (Operator+ only)
  const billingCorrections = entityType === 'ws' && currentStatus === 'BILLING' && !isTechnician
    ? getBillingCorrections(userRole)
    : [];
  const hasBillingCorrections = billingCorrections.length > 0;
  
  // PROMPT 2: Invoiced corrections - includes BILLING, QA_REVIEW, RESCHEDULE (all require notes)
  const invoicedCorrections = entityType === 'ws' && currentStatus === 'INVOICED' && !isTechnician
    ? getInvoicedCorrections(userRole)
    : [];
  const hasInvoicedCorrections = invoicedCorrections.length > 0;
  
  // PROMPT 2: Post-complete corrections (Reschedule from COMPLETE, QA_REVIEW, RELEASE)
  const postCompleteCorrections = entityType === 'ws' && 
    ['COMPLETE', 'QA_REVIEW', 'RELEASE'].includes(currentStatus) && 
    !isTechnician
    ? ['RESCHEDULE']
    : [];
  const hasPostCompleteCorrections = postCompleteCorrections.length > 0;
  
  // Get locked message for technicians or CLOSED/CANCELLED status
  let lockedMessage = null;
  if (entityType === 'ws') {
    if (currentStatus === 'CLOSED') {
      lockedMessage = 'This job is closed and archived.';
    } else if (currentStatus === 'CANCELLED') {
      lockedMessage = 'This job has been cancelled.';
    } else if (isTechnician) {
      lockedMessage = getTechnicianLockedMessage(currentStatus);
    }
  }
  
  // Terminal if no transitions AND no special actions
  const hasNormalTransitions = _validTransitions.length > 0;
  const hasCorrections = hasBillingCorrections || hasInvoicedCorrections || hasPostCompleteCorrections;
  const hasSpecialActions = canCancel || canUncancelStatus || canRescheduleSpecialAction;
  const isTerminal = !hasNormalTransitions && !hasSpecialActions && !hasCorrections && !lockedMessage;

  const followupThreshold = SR_FOLLOWUP_THRESHOLDS[currentStatus];
  const needsFollowup = followupThreshold && daysInStatus >= followupThreshold;

  // Check if COMPLETE should show line items message
  const showLineItemsMessage = entityType === 'ws' && 
    (currentStatus === 'IN_PROCESS' || currentStatus === 'ON_HOLD') &&
    !lineItemsComplete && totalLineItems > 0;

  const handleSubmit = async () => {
    if (!selectedStatus) {
      toast.error('Please select a status');
      return;
    }

    // Handle CANCELLED with confirmation
    if (selectedStatus === 'CANCELLED') {
      if (!cancelReason.trim()) {
        toast.error('Please enter a reason for cancellation');
        return;
      }
      setShowCancelConfirm(true);
      return;
    }

    await performStatusChange(selectedStatus);
  };

  const performStatusChange = async (newStatus, reason = null) => {
    // v1 Railcar Repair Status Notification — intercept the COMPLETE
    // path so the required snapshot form opens BEFORE hitting the
    // status-change endpoint. The snapshot POST flips the WS to
    // COMPLETE server-side once the technician submits the form.
    if (
      entityType === 'ws' &&
      newStatus === 'COMPLETE' &&
      currentStatus !== 'INCOMPLETE' &&   // preserve operator+ override recovery path
      onCompleteIntercept
    ) {
      onOpenChange(false);
      resetForm();
      onCompleteIntercept();
      return;
    }

    setChanging(true);
    try {
      const endpoint = entityType === 'sr' 
        ? `/service-requests/${entityId}/status`
        : `/work-schedules/${entityId}/status`;
      
      // FIX P0 #2: Use overrideNote for INCOMPLETE → COMPLETE override
      const isIncompleteOverride = currentStatus === 'INCOMPLETE' && newStatus === 'COMPLETE';
      const noteToSend = isIncompleteOverride ? overrideNote : (reason || notes || null);
      
      const payload = {
        new_status: newStatus,
        notes: noteToSend
      };

      // Add hold_reason for ON_HOLD
      if (newStatus === 'ON_HOLD') {
        payload.hold_reason = notes || 'No reason provided';
      }
      
      // Add cancellation_reason for CANCELLED
      if (newStatus === 'CANCELLED') {
        payload.cancellation_reason = reason || cancelReason;
      }

      const response = await api.put(endpoint, payload);

      toast.success(response.data.message);
      resetForm();
      onOpenChange(false);
      if (onStatusChange) onStatusChange(newStatus);
    } catch (error) {
      console.error('Status change error:', error);
      toast.error(error.response?.data?.detail || 'Failed to change status');
    } finally {
      setChanging(false);
    }
  };

  const handleConfirmCancel = async () => {
    setShowCancelConfirm(false);
    await performStatusChange('CANCELLED', cancelReason);
  };

  const handleUncancel = async () => {
    if (!previousStatus) {
      toast.error('Cannot uncancel: no previous status recorded');
      return;
    }
    
    setChanging(true);
    try {
      const endpoint = entityType === 'sr' 
        ? `/service-requests/${entityId}/uncancel`
        : `/work-schedules/${entityId}/uncancel`;
      
      const response = await api.put(endpoint, {
        notes: notes || 'Uncancelled'
      });

      toast.success(response.data.message || `Restored to ${previousStatus}`);
      resetForm();
      onOpenChange(false);
      if (onStatusChange) onStatusChange(previousStatus);
    } catch (error) {
      console.error('Uncancel error:', error);
      toast.error(error.response?.data?.detail || 'Failed to uncancel');
    } finally {
      setChanging(false);
    }
  };

  const handleReschedule = async () => {
    // FINAL PROMPT: Status change to Reschedule = tracking only
    // Do NOT open Add Work Schedule page - just change status and save note
    const requiresNote = transitionRequiresNote(currentStatus, 'RESCHEDULE') || isTechnician;
    
    if (requiresNote && !rescheduleReason.trim()) {
      toast.error('Please enter a reason for rescheduling');
      return;
    }
    
    setChanging(true);
    try {
      const response = await api.put(`/work-schedules/${entityId}/status`, {
        new_status: 'RESCHEDULE',
        notes: rescheduleReason || null,
        reschedule_reason: rescheduleReason || null
      });

      toast.success(response.data.message || 'Marked for rescheduling');
      resetForm();
      onOpenChange(false);
      if (onStatusChange) onStatusChange('RESCHEDULE');
    } catch (error) {
      console.error('Reschedule error:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark for rescheduling');
    } finally {
      setChanging(false);
    }
  };

  const resetForm = () => {
    setSelectedStatus('');
    setNotes('');
    setOverrideNote('');
    setCancelReason('');
    setRescheduleReason('');
    setNoteOnlyMode(false);
    setStatusNote('');
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const handleAddStatusNote = async () => {
    if (!statusNote.trim()) {
      toast.error('Please enter a note');
      return;
    }
    setChanging(true);
    try {
      const endpoint = entityType === 'sr'
        ? `/service-requests/${entityId}/status-note`
        : `/work-schedules/${entityId}/status-note`;
      
      await api.post(endpoint, { notes: statusNote.trim() });
      toast.success('Note added to current status');
      resetForm();
      onOpenChange(false);
      if (onStatusChange) onStatusChange(currentStatus);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setChanging(false);
    }
  };

  // Determine if transition is forward or backward (delegates to module-scope helper)
  const transitionDirection = (toStatus) => getTransitionDirection(entityType, currentStatus, toStatus);

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Change Status</DialogTitle>
            <DialogDescription>
              {entityLabel && <span className="font-medium">{entityLabel}</span>}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Current Status */}
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-[#5A5A5A]">Current Status</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${currentConfig.color}`}>
                      {currentConfig.label}
                    </span>
                    {daysInStatus > 0 && (
                      <span className={`flex items-center gap-1 text-sm ${needsFollowup ? 'text-orange-600 font-medium' : 'text-[#5A5A5A]'}`}>
                        <Clock className="w-3.5 h-3.5" />
                        {daysInStatus} day{daysInStatus !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
                {/* Add Note to Current Status toggle — only when status is not terminal */}
                {currentStatus !== 'CLOSED' && currentStatus !== 'CANCELLED' && (
                  <Button
                    type="button"
                    variant={noteOnlyMode ? "default" : "outline"}
                    size="sm"
                    onClick={() => { setNoteOnlyMode(!noteOnlyMode); setSelectedStatus(''); }}
                    data-testid="toggle-note-only"
                  >
                    <MessageSquarePlus className="w-4 h-4 mr-1" />
                    {noteOnlyMode ? 'Cancel Note' : 'Add Note'}
                  </Button>
                )}
              </div>
              {needsFollowup && (
                <div className="mt-2 flex items-center gap-1 text-sm text-orange-600">
                  <AlertCircle className="w-4 h-4" />
                  Needs follow-up (over {followupThreshold} days)
                </div>
              )}
            </div>

            {/* 2026-08-22 — Billing Issue section (WS Billing only, non-technician) */}
            {entityType === 'ws' && currentStatus === 'BILLING' && !isTechnician && (
              <BillingIssueSection
                activeIssue={activeBillingIssue}
                onFlag={async (note) => {
                  const ok = await onFlagBillingIssue?.(note);
                  await _refreshActiveIssue();
                  return ok;
                }}
                onResolve={async (note) => {
                  const ok = await onResolveBillingIssue?.(note);
                  await _refreshActiveIssue();
                  return ok;
                }}
              />
            )}

            {/* Same-Status Note Mode */}
            {noteOnlyMode && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3" data-testid="note-only-section">
                <p className="text-sm font-medium text-blue-800 flex items-center gap-2">
                  <MessageSquarePlus className="w-4 h-4" />
                  Add Note to Current Status
                </p>
                <p className="text-xs text-blue-700">Status will remain <strong>{currentConfig.label}</strong>. A note will be recorded in the status history.</p>
                <textarea
                  className="w-full rounded-md border border-input bg-white px-3 py-2 text-sm min-h-[80px] resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter your note..."
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  data-testid="status-note-input"
                />
                <div className="flex justify-end">
                  <Button
                    onClick={handleAddStatusNote}
                    disabled={changing || !statusNote.trim()}
                    size="sm"
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="submit-status-note"
                  >
                    {changing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <MessageSquarePlus className="w-4 h-4 mr-1" />}
                    Save Note
                  </Button>
                </div>
              </div>
            )}

            {/* Locked Status Message for Technicians */}
            {!noteOnlyMode && lockedMessage && (
              <div className={`p-4 rounded-lg ${currentStatus === 'CLOSED' ? 'bg-gray-100 border border-gray-300' : 'bg-blue-50 border border-blue-200'}`}>
                <p className={`text-sm flex items-center gap-2 ${currentStatus === 'CLOSED' ? 'text-gray-700' : 'text-blue-800'}`}>
                  {currentStatus === 'CLOSED' ? <Archive className="w-4 h-4" /> : <Info className="w-4 h-4" />}
                  {lockedMessage}
                </p>
              </div>
            )}

            {/* RULE 4: Removed fork info banner for COMPLETE status - neutral wording */}

            {/* Terminal State Message for SR_SCHEDULED */}
            {!noteOnlyMode && currentStatus === 'SR_SCHEDULED' && entityType === 'sr' && (
              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  This Service Request has a Work Schedule. Status changes are now made on the Work Schedule.
                </p>
              </div>
            )}

            {/* UNCANCEL Option (Manager+ only, when CANCELLED) */}
            {!noteOnlyMode && canUncancelStatus && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-800 flex items-center gap-2">
                      <Undo2 className="w-4 h-4" />
                      Restore from Cancelled
                    </p>
                    <p className="text-xs text-green-700 mt-1">
                      Will restore to: <strong>{statusConfig[previousStatus]?.label || previousStatus}</strong>
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={handleUncancel}
                    disabled={changing}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {changing ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Uncancel'}
                  </Button>
                </div>
              </div>
            )}

            {/* Line Items Completion Message */}
            {!noteOnlyMode && showLineItemsMessage && (
              <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4 text-gray-400" />
                  Complete all line items to mark this job as complete.
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Progress: {completedLineItems} of {totalLineItems} items completed
                </p>
              </div>
            )}

            {/* Available Standard Transitions */}
            {!noteOnlyMode && hasNormalTransitions && !lockedMessage && (
              <div>
                <Label className="mb-2 block">Move to</Label>
                <div className="grid gap-2">
                  {_validTransitions.map((status) => {
                    const config = statusConfig[status] || { label: status, color: 'bg-gray-100' };
                    const direction = transitionDirection(status);
                    const isSelected = selectedStatus === status;
                    
                    // Get appropriate icon based on status
                    let StatusIcon = direction === 'forward' ? ArrowRight : ArrowLeft;
                    let iconColor = direction === 'forward' ? 'text-green-600' : 'text-orange-600';
                    let description = config.description;
                    
                    // Override icons and descriptions for specific statuses
                    if (status === 'COMPLETE') {
                      StatusIcon = CheckCircle2;
                      iconColor = 'text-green-600';
                      // Context-aware: Override from INCOMPLETE
                      if (currentStatus === 'INCOMPLETE') {
                        description = 'Override incomplete status - note required';
                      }
                    } else if (status === 'QA_REVIEW') {
                      StatusIcon = ClipboardCheck;
                      iconColor = 'text-purple-600';
                      // RULE 4: Use exact wording
                      description = 'Send to Quality Assurance for review';
                    } else if (status === 'RELEASE') {
                      StatusIcon = Unlock;
                      iconColor = 'text-sky-600';
                      // RULE 4: Use exact wording
                      description = 'Release car back to customer contact';
                    } else if (status === 'BILLING') {
                      StatusIcon = DollarSign;
                      iconColor = 'text-orange-600';
                      // Context-aware description: different when coming from INVOICED
                      if (currentStatus === 'INVOICED') {
                        description = 'Return to billing to make corrections';
                      } else {
                        // RULE 4: Use exact wording for forward transition
                        description = 'Billing in progress';
                      }
                    } else if (status === 'INVOICED') {
                      StatusIcon = Receipt;
                      iconColor = 'text-cyan-600';
                    } else if (status === 'CLOSED') {
                      StatusIcon = Archive;
                      iconColor = 'text-gray-600';
                      description = 'Final, job lifecycle complete';
                    }
                    
                    return (
                      <button
                        key={status}
                        onClick={() => setSelectedStatus(status)}
                        data-testid={`status-option-${status}`}
                        className={`p-3 rounded-lg border text-left transition-all flex items-center justify-between ${
                          isSelected 
                            ? 'border-[#005EF8] bg-[#005EF8]/5 ring-2 ring-[#005EF8]/20'
                            : 'border-border hover:border-[#005EF8]/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <StatusIcon className={`w-4 h-4 ${iconColor}`} />
                          <div>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                              {config.label}
                            </span>
                            {description && (
                              <p className="text-xs text-[#5A5A5A] mt-1">{description}</p>
                            )}
                          </div>
                        </div>
                        {isSelected && (
                          <div className="w-5 h-5 rounded-full bg-[#005EF8] flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Billing Corrections Section (Operator+ only, from BILLING status) */}
            {/* FIX 3: Remove descriptions from correction buttons */}
            {hasBillingCorrections && (
              <div className="border-t pt-4 mt-4">
                <Label className="mb-2 block text-[#5A5A5A] flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Corrections
                </Label>
                <div className="grid gap-2">
                  {billingCorrections.map((status) => {
                    const config = statusConfig[status] || { label: status, color: 'bg-gray-100' };
                    const isSelected = selectedStatus === status;
                    const StatusIcon = status === 'QA_REVIEW' ? ClipboardCheck : Unlock;
                    
                    return (
                      <button
                        key={`correction-${status}`}
                        onClick={() => setSelectedStatus(status)}
                        data-testid={`correction-option-${status}`}
                        className={`p-3 rounded-lg border text-left transition-all flex items-center justify-between ${
                          isSelected 
                            ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                            : 'border-amber-200 hover:border-amber-400 bg-amber-50/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <StatusIcon className="w-4 h-4 text-amber-600" />
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                            Return to {config.label}
                          </span>
                        </div>
                        {isSelected && (
                          <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* PROMPT 2: Invoiced Corrections Section (Operator+ only, from INVOICED status) */}
            {/* FIX 3: Only Return to Billing shown - linear path */}
            {hasInvoicedCorrections && (
              <div className="border-t pt-4 mt-4">
                <Label className="mb-2 block text-[#5A5A5A] flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Corrections
                </Label>
                <div className="grid gap-2">
                  {invoicedCorrections.map((status) => {
                    const config = statusConfig[status] || { label: status, color: 'bg-gray-100' };
                    const isSelected = selectedStatus === status;
                    const StatusIcon = Receipt;
                    
                    return (
                      <button
                        key={`invoiced-correction-${status}`}
                        onClick={() => setSelectedStatus(status)}
                        data-testid={`correction-option-${status}`}
                        className={`p-3 rounded-lg border text-left transition-all flex items-center justify-between ${
                          isSelected 
                            ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                            : 'border-amber-200 hover:border-amber-400 bg-amber-50/50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <StatusIcon className="w-4 h-4 text-amber-600" />
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
                            Return to {config.label}
                          </span>
                        </div>
                        {isSelected && (
                          <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* PROMPT 2: Post-Complete Corrections Section (Reschedule from COMPLETE, QA_REVIEW, RELEASE) */}
            {/* FIX 3: Remove descriptions from correction buttons */}
            {hasPostCompleteCorrections && (
              <div className="border-t pt-4 mt-4">
                <Label className="mb-2 block text-[#5A5A5A] flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                  Corrections
                </Label>
                <div className="grid gap-2">
                  <button
                    onClick={() => setSelectedStatus('RESCHEDULE')}
                    data-testid="correction-option-RESCHEDULE"
                    className={`p-3 rounded-lg border text-left transition-all flex items-center justify-between ${
                      selectedStatus === 'RESCHEDULE'
                        ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                        : 'border-amber-200 hover:border-amber-400 bg-amber-50/50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <CalendarClock className="w-4 h-4 text-amber-600" />
                      <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        Reschedule
                      </span>
                    </div>
                    {selectedStatus === 'RESCHEDULE' && (
                      <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Special Actions Section */}
            {!noteOnlyMode && (canCancel || canRescheduleSpecialAction) && !lockedMessage && (
              <div className="border-t pt-4 mt-4">
                <Label className="mb-2 block text-[#5A5A5A]">Special Actions</Label>
                <div className="grid gap-2">
                  {/* RESCHEDULE - Only for early-stage statuses (SCHEDULED, IN_PROCESS, ON_HOLD) */}
                  {canRescheduleSpecialAction && (
                    <button
                      onClick={() => setSelectedStatus('RESCHEDULE')}
                      data-testid="status-option-RESCHEDULE"
                      className={`p-3 rounded-lg border text-left transition-all flex items-center justify-between ${
                        selectedStatus === 'RESCHEDULE'
                          ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
                          : 'border-border hover:border-amber-400'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <CalendarClock className="w-4 h-4 text-amber-600" />
                        <div>
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                            Reschedule
                          </span>
                          <p className="text-xs text-[#5A5A5A] mt-1">
                            {isTechnician 
                              ? 'Request rescheduling - an operator will update the dates'
                              : 'Mark for rescheduling and update dates later'
                            }
                          </p>
                        </div>
                      </div>
                      {selectedStatus === 'RESCHEDULE' && (
                        <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </button>
                  )}

                  {/* CANCELLED (Operator+ only - NOT shown to technicians) */}
                  {canCancel && (
                    <button
                      onClick={() => setSelectedStatus('CANCELLED')}
                      data-testid="status-option-CANCELLED"
                      className={`p-3 rounded-lg border text-left transition-all flex items-center justify-between ${
                        selectedStatus === 'CANCELLED'
                          ? 'border-red-500 bg-red-50 ring-2 ring-red-200'
                          : 'border-border hover:border-red-400'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <XCircle className="w-4 h-4 text-red-600" />
                        <div>
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                            Cancel
                          </span>
                          <p className="text-xs text-[#5A5A5A] mt-1">
                            Cancel this {entityType === 'sr' ? 'service request' : 'work schedule'}
                          </p>
                        </div>
                      </div>
                      {selectedStatus === 'CANCELLED' && (
                        <div className="w-5 h-5 rounded-full bg-red-500 flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Reschedule Reason - PROMPT 2: Required for post-complete statuses */}
            {selectedStatus === 'RESCHEDULE' && (
              <div>
                {/* Determine if note is required based on current status */}
                {(() => {
                  const requiresNote = transitionRequiresNote(currentStatus, 'RESCHEDULE') || isTechnician;
                  return (
                    <>
                      <Label htmlFor="rescheduleReason">
                        Reason for Reschedule {requiresNote ? '*' : '(Optional)'}
                      </Label>
                      <Textarea
                        id="rescheduleReason"
                        value={rescheduleReason}
                        onChange={(e) => setRescheduleReason(e.target.value)}
                        placeholder="e.g., Per customer, cars not in position for repair. Reschedule requested for next available date."
                        rows={3}
                        className="mt-1"
                        data-testid="reschedule-reason-input"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {requiresNote 
                          ? 'Required. Provide a reason for this backward status change.'
                          : 'Optional. Provide a reason for rescheduling this work order.'
                        }
                      </p>
                    </>
                  );
                })()}
              </div>
            )}

            {/* PROMPT 2: Correction Note (Required for backward moves from BILLING and INVOICED) */}
            {(billingCorrections.includes(selectedStatus) || 
              (invoicedCorrections.includes(selectedStatus) && selectedStatus !== 'RESCHEDULE')) && (
              <div>
                <Label htmlFor="correctionNote">Correction Note *</Label>
                <Textarea
                  id="correctionNote"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Explain why this correction is needed..."
                  rows={2}
                  className="mt-1"
                  data-testid="correction-note-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Required. This note will be logged in the work order history.
                </p>
              </div>
            )}

            {/* FIX P0 #2: Override Note uses separate overrideNote state for INCOMPLETE → COMPLETE */}
            {selectedStatus === 'COMPLETE' && currentStatus === 'INCOMPLETE' && (
              <div>
                <Label htmlFor="overrideNote">Override Note *</Label>
                <Textarea
                  id="overrideNote"
                  value={overrideNote}
                  onChange={(e) => setOverrideNote(e.target.value)}
                  rows={2}
                  className="mt-1"
                  data-testid="override-note-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Required. Explain why incomplete items are being accepted.
                </p>
              </div>
            )}

            {/* Cancellation Reason (Required) */}
            {selectedStatus === 'CANCELLED' && (
              <div>
                <Label htmlFor="cancelReason">Reason for Cancellation *</Label>
                <Textarea
                  id="cancelReason"
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  placeholder="Why is this being cancelled?"
                  rows={2}
                  className="mt-1"
                  data-testid="cancel-reason-input"
                />
              </div>
            )}

            {/* Notes for other transitions */}
            {!noteOnlyMode && selectedStatus && 
             selectedStatus !== 'CANCELLED' && 
             selectedStatus !== 'RESCHEDULE' && 
             !(selectedStatus === 'COMPLETE' && currentStatus === 'INCOMPLETE') &&
             !billingCorrections.includes(selectedStatus) &&
             !invoicedCorrections.includes(selectedStatus) && (
              <div>
                <Label htmlFor="notes">
                  {selectedStatus === 'ON_HOLD' ? 'Hold Reason *' : 'Notes (Optional)'}
                </Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={
                    selectedStatus === 'ON_HOLD' 
                      ? 'Why is this being put on hold?' 
                      : 'Add any notes about this status change...'
                  }
                  rows={2}
                  className="mt-1"
                />
              </div>
            )}

            {/* No Actions Available */}
            {isTerminal && !canUncancelStatus && !lockedMessage && (
              <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                <p className="text-sm text-gray-600">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  No status changes available.
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={handleClose}>
              Close
            </Button>
            {!noteOnlyMode && selectedStatus && selectedStatus !== 'RESCHEDULE' && (
              <Button
                onClick={handleSubmit}
                disabled={
                  !selectedStatus || 
                  changing || 
                  (selectedStatus === 'ON_HOLD' && !notes.trim()) ||
                  (selectedStatus === 'CANCELLED' && !cancelReason.trim()) ||
                  (selectedStatus === 'COMPLETE' && currentStatus === 'INCOMPLETE' && !overrideNote.trim()) ||
                  // PROMPT 2: Require notes for billing/invoiced corrections
                  (billingCorrections.includes(selectedStatus) && !notes.trim()) ||
                  (invoicedCorrections.includes(selectedStatus) && !notes.trim())
                }
                className={submitButtonClass({ selectedStatus, billingCorrections, invoicedCorrections })}
                data-testid="confirm-status-change-btn"
              >
                {changing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Changing...
                  </>
                ) : (
                  submitButtonLabel({ selectedStatus, currentStatus, billingCorrections, invoicedCorrections })
                )}
              </Button>
            )}
            {selectedStatus === 'RESCHEDULE' && (
              <Button
                onClick={handleReschedule}
                disabled={changing || (transitionRequiresNote(currentStatus, 'RESCHEDULE') || isTechnician) && !rescheduleReason.trim()}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="confirm-reschedule-btn"
              >
                {changing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  'Request Reschedule'
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog */}
      <CancelConfirmDialog
        open={showCancelConfirm}
        onOpenChange={setShowCancelConfirm}
        entityType={entityType}
        reason={cancelReason}
        onGoBack={() => {
          setShowCancelConfirm(false);
          setSelectedStatus('');
        }}
        onConfirm={handleConfirmCancel}
      />
    </>
  );
};

export default StatusChangeModal;
